/**
 * useLandingSLKflowLandingPendulumLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait LandingSLKflowLandingPendulum
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, LandingSLPendulumState, LandingSLKflowLandingPendulumEventPayloadMap } from '@app/shared';

export interface LandingSLKflowLandingPendulumConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  backgroundColor: string;
  bodies: { color: string; fx: number; fy: number; id: string; label: string; radius: number; vx: number; vy: number; x: number; y: number }[];
  damping: number;
  gravity: number;
  height: number;
  length: number;
  matchMode: string;
  pivotXFrac: number;
  pivotYFrac: number;
  showVelocity: boolean;
  substeps: number;
  traceLength: number;
  vectorScale: number;
  velocityScale: number;
  width: number;
}

const DEFAULT_LANDING_SL_KFLOW_LANDING_PENDULUM_CONFIG = {
  backgroundColor: "",
  bodies: [{ color: "series-1", fx: 0, fy: 0, id: "bob", label: "", radius: 18, vx: 0, vy: 0, x: 450, y: 110 }],
  damping: 0.995,
  gravity: 0.5,
  height: 400,
  length: 170,
  matchMode: "",
  pivotXFrac: 0.5,
  pivotYFrac: 0.175,
  showVelocity: true,
  substeps: 8,
  traceLength: 60,
  vectorScale: 120,
  velocityScale: 5,
  width: 600,
} as LandingSLKflowLandingPendulumConfig;

type LandingSLKflowLandingPendulumSlot = 'main';
export interface LandingSLKflowLandingPendulumLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<LandingSLKflowLandingPendulumSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingSLPendulumState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingSLKflowLandingPendulumEventPayloadMap]: (payload?: LandingSLKflowLandingPendulumEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingSLKflowLandingPendulumConfig;
}

export function useLandingSLKflowLandingPendulumLogic(config?: Partial<LandingSLKflowLandingPendulumConfig>): LandingSLKflowLandingPendulumLogicReturn {
  const mergedConfig: LandingSLKflowLandingPendulumConfig = { ...DEFAULT_LANDING_SL_KFLOW_LANDING_PENDULUM_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'LandingSLPendulumState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<LandingSLPendulumState>;
  const data: Record<string, EntityRow[]> = { 'LandingSLPendulumState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<LandingSLKflowLandingPendulumSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingSLPendulumState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const pivotX = (600 * 0.5);

        const pivotY = (400 * 0.175);

        const configBob = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "bob" as const, x: 450, y: 110, radius: 18, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }], (0));

        const bobId = getNestedValue(configBob, "id");

        const bobX = getNestedValue(configBob, "x");

        const bobY = getNestedValue(configBob, "y");

        const dx = (+(bobX ?? 0) - +(pivotX ?? 0));

        const dy = (+(bobY ?? 0) - +(pivotY ?? 0));

        const dist = Math.hypot(Number(dx ?? 0), Number(dy ?? 0));

        const bearingDeg = ((Math.atan2(Number(dy ?? 0), Number(dx ?? 0))) * (180 / Math.PI));

        const thetaFromVertical = (+(bearingDeg ?? 0) - 90);

        const thetaLabel = [["", +((Number(thetaFromVertical ?? 0)).toFixed((1)))].join(''), "°"].join('');

        const tMag = (0.5 * (120 * Math.cos(((Number(thetaFromVertical ?? 0) * (Math.PI / 180))))));

        const ux = ((-1 * +(dx ?? 0)) / +(dist ?? 0));

        const uy = ((-1 * +(dy ?? 0)) / +(dist ?? 0));

        _fields['sceneObjects'] = [{ kind: "pivot" as const, x: pivotX, y: pivotY, label: "pivot" as const }];
        fields = { ...(fields ?? {}), 'sceneObjects': _fields['sceneObjects'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [...([{ id: "pivot" as const, x: pivotX, y: pivotY, radius: 6, color: "ink" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }]), ...([configBob])];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['constraints'] = [{ from: "pivot" as const, to: bobId, kind: "rod" as const, color: "ink" as const }];
        fields = { ...(fields ?? {}), 'constraints': _fields['constraints'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = [{ id: bobId, points: [{ x: bobX, y: bobY }], color: getNestedValue(configBob, "color") }];
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        _fields['vectors'] = [{ body: bobId, dx: 0, dy: (0.5 * 120), color: "series-5" as const, label: "mg" as const }, { body: bobId, dx: (+(ux ?? 0) * +(tMag ?? 0)), dy: (+(uy ?? 0) * +(tMag ?? 0)), color: "series-6" as const, label: "T" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        _fields['angles'] = [{ x: pivotX, y: pivotY, from: 90, to: bearingDeg, radius: 24, color: "series-2" as const, label: thetaLabel }];
        fields = { ...(fields ?? {}), 'angles': _fields['angles'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "θ" as const, value: thetaLabel }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["ready — bob at ", [thetaLabel, " from vertical; press Step or Run to swing"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('PENDULUM_UPDATED', _p, { trait: 'LandingSLKflowLandingPendulum' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingPendulum', { event: 'PENDULUM_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingPendulum' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const pivotX = (600 * 0.5);

        const pivotY = (400 * 0.175);

        const configBob = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "bob" as const, x: 450, y: 110, radius: 18, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }], (0));

        const bobId = getNestedValue(configBob, "id");

        const bobX = getNestedValue(configBob, "x");

        const bobY = getNestedValue(configBob, "y");

        const dx = (+(bobX ?? 0) - +(pivotX ?? 0));

        const dy = (+(bobY ?? 0) - +(pivotY ?? 0));

        const dist = Math.hypot(Number(dx ?? 0), Number(dy ?? 0));

        const bearingDeg = ((Math.atan2(Number(dy ?? 0), Number(dx ?? 0))) * (180 / Math.PI));

        const thetaFromVertical = (+(bearingDeg ?? 0) - 90);

        const thetaLabel = [["", +((Number(thetaFromVertical ?? 0)).toFixed((1)))].join(''), "°"].join('');

        const tMag = (0.5 * (120 * Math.cos(((Number(thetaFromVertical ?? 0) * (Math.PI / 180))))));

        const ux = ((-1 * +(dx ?? 0)) / +(dist ?? 0));

        const uy = ((-1 * +(dy ?? 0)) / +(dist ?? 0));

        _fields['sceneObjects'] = [{ kind: "pivot" as const, x: pivotX, y: pivotY, label: "pivot" as const }];
        fields = { ...(fields ?? {}), 'sceneObjects': _fields['sceneObjects'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [...([{ id: "pivot" as const, x: pivotX, y: pivotY, radius: 6, color: "ink" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }]), ...([configBob])];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['constraints'] = [{ from: "pivot" as const, to: bobId, kind: "rod" as const, color: "ink" as const }];
        fields = { ...(fields ?? {}), 'constraints': _fields['constraints'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = [{ id: bobId, points: [{ x: bobX, y: bobY }], color: getNestedValue(configBob, "color") }];
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        _fields['vectors'] = [{ body: bobId, dx: 0, dy: (0.5 * 120), color: "series-5" as const, label: "mg" as const }, { body: bobId, dx: (+(ux ?? 0) * +(tMag ?? 0)), dy: (+(uy ?? 0) * +(tMag ?? 0)), color: "series-6" as const, label: "T" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        _fields['angles'] = [{ x: pivotX, y: pivotY, from: 90, to: bearingDeg, radius: 24, color: "series-2" as const, label: thetaLabel }];
        fields = { ...(fields ?? {}), 'angles': _fields['angles'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "θ" as const, value: thetaLabel }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["ready — bob at ", [thetaLabel, " from vertical; press Step or Run to swing"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('PENDULUM_UPDATED', _p, { trait: 'LandingSLKflowLandingPendulum' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingPendulum', { event: 'PENDULUM_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingPendulum' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on LEARN_STEP)
        {

        const pivotX = (600 * 0.5);

        const pivotY = (400 * 0.175);

        const newBodies = (Array.from({ length: Math.ceil(8) }, (_, i) => i)).reduce<EventPayloadValue>((acc, s) => ((acc ?? []) as readonly EntityRow[]).map((b) => ((getNestedValue(b, "id") === "pivot") ? b : (() => { const nvy = ((+(getNestedValue(b, "vy") ?? 0) + 0.5) * 0.995); const nvx = (+(getNestedValue(b, "vx") ?? 0) * 0.995); return (() => { const px = (+(getNestedValue(b, "x") ?? 0) + +(nvx ?? 0)); const py = (+(getNestedValue(b, "y") ?? 0) + +(nvy ?? 0)); return (() => { const rdx = (+(px ?? 0) - +(pivotX ?? 0)); const rdy = (+(py ?? 0) - +(pivotY ?? 0)); return (() => { const rdist = Math.hypot(Number(rdx ?? 0), Number(rdy ?? 0)); return (() => { const s = (170 / +(rdist ?? 0)); return (() => { const cxp = (+(pivotX ?? 0) + (+(rdx ?? 0) * +(s ?? 0))); const cyp = (+(pivotY ?? 0) + (+(rdy ?? 0) * +(s ?? 0))); return Object.assign({}, b, { x: cxp, y: cyp, vx: (+(cxp ?? 0) - +(getNestedValue(b, "x") ?? 0)), vy: (+(cyp ?? 0) - +(getNestedValue(b, "y") ?? 0)) }); })(); })(); })(); })(); })(); })())), fields?.bodies);

        const bob = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(newBodies, (1));

        const bobId = getNestedValue(bob, "id");

        const bobX = getNestedValue(bob, "x");

        const bobY = getNestedValue(bob, "y");

        const dx = (+(bobX ?? 0) - +(pivotX ?? 0));

        const dy = (+(bobY ?? 0) - +(pivotY ?? 0));

        const dist = Math.hypot(Number(dx ?? 0), Number(dy ?? 0));

        const bearingDeg = ((Math.atan2(Number(dy ?? 0), Number(dx ?? 0))) * (180 / Math.PI));

        const thetaFromVertical = (+(bearingDeg ?? 0) - 90);

        const thetaLabel = [["", +((Number(thetaFromVertical ?? 0)).toFixed((1)))].join(''), "°"].join('');

        const tMag = (0.5 * (120 * Math.cos(((Number(thetaFromVertical ?? 0) * (Math.PI / 180))))));

        const ux = ((-1 * +(dx ?? 0)) / +(dist ?? 0));

        const uy = ((-1 * +(dy ?? 0)) / +(dist ?? 0));

        const prevPts = getNestedValue(((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(fields?.trails, (0)), "points");

        const appended = [...((prevPts as readonly EventPayloadValue[] | undefined) ?? []), { x: bobX, y: bobY }];

        const n = ((appended ?? []) as { readonly length: number }).length;

        _fields['bodies'] = newBodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = [{ id: bobId, points: ((+(n ?? 0) > 60) ? ((appended as readonly EventPayloadValue[] | undefined) ?? []).slice((+(n ?? 0) - 60)) : appended), color: getNestedValue(bob, "color") }];
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        _fields['vectors'] = [{ body: bobId, dx: 0, dy: (0.5 * 120), color: "series-5" as const, label: "mg" as const }, { body: bobId, dx: (+(ux ?? 0) * +(tMag ?? 0)), dy: (+(uy ?? 0) * +(tMag ?? 0)), color: "series-6" as const, label: "T" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        _fields['angles'] = [{ x: pivotX, y: pivotY, from: 90, to: bearingDeg, radius: 24, color: "series-2" as const, label: thetaLabel }];
        fields = { ...(fields ?? {}), 'angles': _fields['angles'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "θ" as const, value: thetaLabel }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["swinging — ", [thetaLabel, " from vertical"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('PENDULUM_UPDATED', _p, { trait: 'LandingSLKflowLandingPendulum' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingPendulum', { event: 'PENDULUM_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingPendulum' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET' && ((fields?.active === true))) {
      // idle → idle (on LEARN_RESET)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        {

        const pivotX = (600 * 0.5);

        const pivotY = (400 * 0.175);

        const configBob = ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)([{ id: "bob" as const, x: 450, y: 110, radius: 18, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }], (0));

        const bobId = getNestedValue(configBob, "id");

        const bobX = getNestedValue(configBob, "x");

        const bobY = getNestedValue(configBob, "y");

        const dx = (+(bobX ?? 0) - +(pivotX ?? 0));

        const dy = (+(bobY ?? 0) - +(pivotY ?? 0));

        const dist = Math.hypot(Number(dx ?? 0), Number(dy ?? 0));

        const bearingDeg = ((Math.atan2(Number(dy ?? 0), Number(dx ?? 0))) * (180 / Math.PI));

        const thetaFromVertical = (+(bearingDeg ?? 0) - 90);

        const thetaLabel = [["", +((Number(thetaFromVertical ?? 0)).toFixed((1)))].join(''), "°"].join('');

        const tMag = (0.5 * (120 * Math.cos(((Number(thetaFromVertical ?? 0) * (Math.PI / 180))))));

        const ux = ((-1 * +(dx ?? 0)) / +(dist ?? 0));

        const uy = ((-1 * +(dy ?? 0)) / +(dist ?? 0));

        _fields['sceneObjects'] = [{ kind: "pivot" as const, x: pivotX, y: pivotY, label: "pivot" as const }];
        fields = { ...(fields ?? {}), 'sceneObjects': _fields['sceneObjects'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = [...([{ id: "pivot" as const, x: pivotX, y: pivotY, radius: 6, color: "ink" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 }]), ...([configBob])];
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['constraints'] = [{ from: "pivot" as const, to: bobId, kind: "rod" as const, color: "ink" as const }];
        fields = { ...(fields ?? {}), 'constraints': _fields['constraints'] } as Record<string, EventPayloadValue | undefined>;
        _fields['trails'] = [{ id: bobId, points: [{ x: bobX, y: bobY }], color: getNestedValue(configBob, "color") }];
        fields = { ...(fields ?? {}), 'trails': _fields['trails'] } as Record<string, EventPayloadValue | undefined>;
        _fields['vectors'] = [{ body: bobId, dx: 0, dy: (0.5 * 120), color: "series-5" as const, label: "mg" as const }, { body: bobId, dx: (+(ux ?? 0) * +(tMag ?? 0)), dy: (+(uy ?? 0) * +(tMag ?? 0)), color: "series-6" as const, label: "T" as const }];
        fields = { ...(fields ?? {}), 'vectors': _fields['vectors'] } as Record<string, EventPayloadValue | undefined>;
        _fields['angles'] = [{ x: pivotX, y: pivotY, from: 90, to: bearingDeg, radius: 24, color: "series-2" as const, label: thetaLabel }];
        fields = { ...(fields ?? {}), 'angles': _fields['angles'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "θ" as const, value: thetaLabel }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["ready — bob at ", [thetaLabel, " from vertical; press Step or Run to swing"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('PENDULUM_UPDATED', _p, { trait: 'LandingSLKflowLandingPendulum' }); emitCascadeEvent(eventBus, 'LandingSL', 'LandingSLKflowLandingPendulum', { event: 'PENDULUM_UPDATED', payload: _p, source: { orbital: 'LandingSL', trait: 'LandingSLKflowLandingPendulum' } }); })({ bodies: fields?.bodies, trails: fields?.trails, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_UPDATED' && ((fields?.active === true))) {
      // idle → idle (on LEARN_UPDATED)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingSLKflowLandingPendulumSlot[]; clear: LandingSLKflowLandingPendulumSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingSLKflowLandingPendulumSlot[], clear: [] as LandingSLKflowLandingPendulumSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as LandingSLKflowLandingPendulumSlot[], clear: [] as LandingSLKflowLandingPendulumSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as LandingSLKflowLandingPendulumSlot[], clear: [] as LandingSLKflowLandingPendulumSlot[] };
          case 'LEARN_UPDATED': return { write: ['main'] as LandingSLKflowLandingPendulumSlot[], clear: [] as LandingSLKflowLandingPendulumSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingSLKflowLandingPendulumSlot[], clear: ['main'] as LandingSLKflowLandingPendulumSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'LandingSLPendulumState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'LandingSLKflowLandingPendulum': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingSLKflowLandingPendulumEventPayloadMap]: (payload?: LandingSLKflowLandingPendulumEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingSLKflowLandingPendulumEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'LEARN_STEP': (payload?: LandingSLKflowLandingPendulumEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: LandingSLKflowLandingPendulumEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
    'PENDULUM_UPDATED': (payload?: LandingSLKflowLandingPendulumEventPayloadMap['PENDULUM_UPDATED']) => dispatch('PENDULUM_UPDATED', payload),
    'RESET': () => dispatch('RESET'),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
  };

  useUIEvents(enqueueEvent, 'LandingSL.LandingSLKflowLandingPendulum', ['INIT', 'MODE_SELECTED', 'LEARN_STEP', 'LEARN_RESET', 'LEARN_UPDATED'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_RESET' (from LandingSLKflowLandingPendulumClock) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_landingslkflowlandingpendulumclock_to_learn_reset = eventBus.on('UI:LandingSL.LandingSLKflowLandingPendulumClock.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingPendulum', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    // Listen for 'LEARN_STEP' (from LandingSLKflowLandingPendulumClock) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_landingslkflowlandingpendulumclock_to_learn_step = eventBus.on('UI:LandingSL.LandingSLKflowLandingPendulumClock.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingPendulum', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_UPDATED' (from LandingSLKflowLandingPendulumClock) → trigger 'LEARN_UPDATED'
    const unsub_learn_updated_from_landingslkflowlandingpendulumclock_to_learn_updated = eventBus.on('UI:LandingSL.LandingSLKflowLandingPendulumClock.LEARN_UPDATED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingSL.LandingSLKflowLandingPendulum', 'LEARN_UPDATED')) return;
      enqueueEvent('LEARN_UPDATED', event.payload);
    });
    return () => {
      if (typeof unsub_learn_reset_from_landingslkflowlandingpendulumclock_to_learn_reset === 'function') unsub_learn_reset_from_landingslkflowlandingpendulumclock_to_learn_reset();
      if (typeof unsub_learn_step_from_landingslkflowlandingpendulumclock_to_learn_step === 'function') unsub_learn_step_from_landingslkflowlandingpendulumclock_to_learn_step();
      if (typeof unsub_learn_updated_from_landingslkflowlandingpendulumclock_to_learn_updated === 'function') unsub_learn_updated_from_landingslkflowlandingpendulumclock_to_learn_updated();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
